/**
 * Upload API - Uses apiFetch for consistent auth
 */

import { apiFetch, toUserError } from "./httpClient";

/**
 * Upload multiple images
 * Uses apiFetch which ALWAYS includes auth token
 */
export async function uploadImages(files = []) {
  if (!files || files.length === 0) {
    return [];
  }
  
  const urls = [];

  for (const fileOrObj of files) {
    // Handle both File objects and {file, previewUrl} objects
    const file = fileOrObj instanceof File ? fileOrObj : fileOrObj?.file;
    
    // Skip if no actual file (might be pre-uploaded URL)
    if (!file) {
      const existingUrl = fileOrObj?.url || fileOrObj?.previewUrl;
      if (existingUrl && !existingUrl.startsWith('blob:')) {
        urls.push(existingUrl);
      }
      continue;
    }

    const fd = new FormData();
    fd.append("files", file); // Backend expects "files"

    const r = await apiFetch("/api/upload", { method: "POST", body: fd, timeoutMs: 90000 });

    if (!r.ok) {
      // iter282 — Surface the *actual* backend reason on upload failure
      // so the user / operator can see whether it's an auth / size /
      // Cloudinary / disk / network problem at a glance, instead of
      // the generic "Image upload failed" toast.
      const baseMsg = toUserError(r);
      // The backend wraps multi-file failures as
      // detail: { code, message, errors: [{index, name, error}], diagnostic }
      const detailObj = (r?.data && typeof r.data.detail === 'object') ? r.data.detail : null;
      let firstErr = '';
      if (detailObj?.errors && Array.isArray(detailObj.errors) && detailObj.errors.length > 0) {
        firstErr = detailObj.errors[0]?.error || '';
      }
      const diag = detailObj?.diagnostic
        ? ` [cloudinary=${detailObj.diagnostic.cloudinary_configured ? 'on' : 'off'}]`
        : '';
      const msg = firstErr
        ? `${baseMsg}: ${firstErr}${diag}`
        : (r.status === 0
            ? 'Network error — could not reach upload endpoint'
            : `${baseMsg} (HTTP ${r.status})${diag}`);
      console.error('[Upload] FAILED', {
        status: r.status,
        requestId: r.requestId,
        detail: r.data?.detail,
      });
      const err = new Error(msg);
      err.status = r.status;
      err.stage = 'upload';
      err.requestId = r.requestId;
      throw err;
    }

    // Backend returns { images: [...] }
    const uploadedUrl = r.data?.images?.[0] || r.data?.url || r.data?.fileUrl || r.data?.path;
    if (!uploadedUrl) {
      throw new Error("Upload succeeded but server returned no url.");
    }
    urls.push(uploadedUrl);
  }

  console.log(`[Upload] Completed: ${urls.length} images`);
  return urls;
}

export default { uploadImages };
